import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, MessageDescriptor, defineMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button, {
  Props as ButtonProps,
} from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import Dialog from '@sorare/core/src/components/dialog';
import { FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_INVITATION_CODE } from '@sorare/core/src/constants/__generated__/routes';
import { useIsMobile } from '@sorare/core/src/hooks/device/useIsMobile';

import { InviteContent } from 'components/rivalsUserGroup/InviteContent';

import { AddFriends_rivalsUserGroup } from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  position: relative;
  padding: var(--double-unit);
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: var(--triple-unit);
  right: var(--triple-unit);
`;

const addFriends = defineMessage({
  id: 'UserGroupActivity.addFriends',
  defaultMessage: 'Add friends',
});

type Props = {
  userGroup: AddFriends_rivalsUserGroup;
  onClick?: () => void;
  label?: MessageDescriptor;
  buttonSize?: ButtonProps['size'];
};
export const AddFriends = ({
  userGroup,
  onClick,
  label,
  buttonSize = 'small',
}: Props) => {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const onSubmit = () => {
    onClick?.();
    if (isMobile) {
      navigate(
        generatePath(FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_INVITATION_CODE, {
          slug: userGroup.slug,
        })
      );
    } else {
      setOpen(true);
    }
  };
  const onClose = () => setOpen(false);

  return (
    <>
      <Button onClick={onSubmit} color="secondary" size={buttonSize}>
        <FormattedMessage {...(label || addFriends)} />
      </Button>
      {!isMobile && (
        <Dialog
          maxWidth="sm"
          fullWidth
          open={open}
          onClose={onClose}
          hideHeader
        >
          {({ CloseButton }) => (
            <Wrapper>
              <CloseButtonWrapper>
                <CloseButton onClose={onClose} />
              </CloseButtonWrapper>
              <InviteContent userGroup={userGroup} />
            </Wrapper>
          )}
        </Dialog>
      )}
    </>
  );
};

AddFriends.fragments = {
  rivalsUserGroup: gql`
    fragment AddFriends_rivalsUserGroup on RivalsUserGroup {
      slug
      id
      ...InviteContent_rivalsUserGroup
    }
    ${InviteContent.fragments.rivalsUserGroup}
  ` as TypedDocumentNode<AddFriends_rivalsUserGroup>,
};
